import * as amplitude from '@amplitude/analytics-browser';

const APP_AMPLITUDE_API_KEY = window.ENV?.APP_AMPLITUDE_API_KEY;
const COOKIE_CONSENT_KEY = 'userHasGivenCookieConsent';
const CONSENT_EXPIRATION_DAYS = 365;

let isAmplitudeFullyInitialized = false;
let amplitudeReadyCallbacks = [];

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

export const setupAmplitudeInitially = () => {
    if (!APP_AMPLITUDE_API_KEY) return;

    try {
        amplitude.init(APP_AMPLITUDE_API_KEY, undefined, {
            serverZone: 'EU',
            deferInitialization: true,
            defaultTracking: false, // deprecated
            autocapture: false,
        });
    } catch (error) {
        return;
    }
};

export const enableAmplitudeTracking = () => {
    setConsentInStorage(true);
    initializeFullAmplitude();

    // Track consent acceptance
    try {
        amplitude.track('Consent: Accepted', {
            consent_type: 'statistics',
            consent_source: 'booking_frontend',
            consent_given: true,
            tracking_mode: 'full',
        });
    } catch (error) {
        return;
    }

    // Notify any waiting callbacks that Amplitude is now ready
    notifyAmplitudeReady();
};

export const optOutAmplitudeTracking = () => {
    // Persist the decision first so an Amplitude error can't drop it (banner would reappear on reload)
    setConsentInStorage(false);

    // Track the rejection event while we can still track
    try {
        // Initialize in anonymous mode to track the rejection
        if (!isAmplitudeFullyInitialized) {
            amplitude.init(APP_AMPLITUDE_API_KEY, undefined, {
                serverZone: 'EU',
                identityStorage: 'none',
            });
        }

        amplitude.track('Consent: Rejected', {
            consent_type: 'statistics',
            consent_source: 'booking_frontend',
            consent_given: false,
            tracking_mode: 'anonymous',
        });
    } catch (error) {
        return;
    }

    // Then initialize anonymous tracking
    isAmplitudeFullyInitialized = false;
    initializeAnonymousAmplitude();
};

export const checkHasGivenConsent = () => {
    const consentData = getConsentFromStorage();
    return consentData ? consentData.consent : false;
};

export const shouldShowBanner = () => {
    return getConsentFromStorage() === null;
};

// Since the BrowserRouter migration these params arrive in window.location.search.
// Fall back to the hash for legacy HashRouter links that predate the migration.
const getUrlParams = () => {
    if (window.location.search) {
        return new URLSearchParams(window.location.search);
    }

    const hash = window.location.hash;
    const queryStringIndex = hash.indexOf('?');
    if (queryStringIndex === -1) {
        return new URLSearchParams();
    }

    return new URLSearchParams(hash.substring(queryStringIndex + 1));
};

export const syncConsentFromUrl = () => {
    const cookieConsentFromUrl = getUrlParams().get('cookieConsent');

    if (cookieConsentFromUrl !== null) {
        const hasConsent = cookieConsentFromUrl === 'true';
        setConsentInStorage(hasConsent);
    }
};

/**
 * Extracts session continuity parameters from URL for Amplitude
 * @returns {Object|null} Object with sessionId and deviceId, or null if not present
 */
export const getSessionParamsFromUrl = () => {
    const urlParams = getUrlParams();
    const sessionId = urlParams.get('ampSessionId');
    const deviceId = urlParams.get('ampDeviceId');

    if (sessionId && deviceId) {
        return {
            sessionId: parseInt(sessionId, 10),
            deviceId: deviceId,
        };
    }

    return null;
};

/**
 * Extracts UTM parameters from URL for tracking attribution.
 * @returns {Object|null} Object with present UTM keys, or null if none found
 */
export const getUtmParamsFromUrl = () => {
    if (!window.location.search) {
        return null;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const params = {};
    UTM_KEYS.forEach((key) => {
        const value = urlParams.get(key);
        if (value) params[key] = value;
    });

    return Object.keys(params).length > 0 ? params : null;
};

/**
 * Captures UTM parameters from URL, stores them for event enrichment,
 * and sets them as Amplitude user properties for attribution analysis.
 * Should be called once when the booking page loads.
 */
export const captureUtmParameters = () => {
    const params = getUtmParamsFromUrl();
    if (params) {
        setUtmUserProperties(params);
        stripUtmParamsFromUrl();
    }
};

const stripUtmParamsFromUrl = () => {
    try {
        const url = new URL(window.location.href);
        let changed = false;

        UTM_KEYS.forEach((key) => {
            if (url.searchParams.has(key)) {
                url.searchParams.delete(key);
                changed = true;
            }
        });

        if (changed) {
            window.history.replaceState(null, '', url.toString());
        }
    } catch (error) {
        // Silently ignore — cosmetic cleanup only
    }
};

const setUtmUserProperties = (params) => {
    try {
        const identifyObj = new amplitude.Identify();

        // "set" overwrites on every visit so we always track the latest campaign.
        // Switch to setOnce() if you prefer first-touch attribution instead.
        Object.entries(params).forEach(([key, value]) => {
            identifyObj.set(key, value);
        });

        amplitude.identify(identifyObj);
    } catch (error) {
        // Amplitude may not be fully initialized yet; the identify call will
        // be queued internally by the SDK and sent once init completes.
    }
};

export const initAmplitudeWithConsentCheck = () => {
    const consentData = getConsentFromStorage();
    if (consentData?.consent) {
        // If consent is true - full tracking
        initializeFullAmplitude();
        // Notify any waiting callbacks that Amplitude is now ready
        notifyAmplitudeReady();
    } else if (consentData?.consent === false) {
        // If consent is explicitly false - anonymous tracking (GDPR compliant)
        initializeAnonymousAmplitude();
        // Notify any waiting callbacks that Amplitude is now ready
        notifyAmplitudeReady();
    }
    // If consentData is null (not decided yet), do nothing. Amplitude remains deferred.
};

/**
 * Register a callback to be called when Amplitude becomes ready
 * If Amplitude is already ready, the callback is called immediately
 */
export const onAmplitudeReady = (callback) => {
    if (isAmplitudeFullyInitialized) {
        callback();
    } else {
        amplitudeReadyCallbacks.push(callback);
    }
};

/**
 * Internal function to notify all waiting callbacks that Amplitude is ready
 */
const notifyAmplitudeReady = () => {
    amplitudeReadyCallbacks.forEach((callback) => {
        try {
            callback();
        } catch (error) {
            console.error('Error in Amplitude ready callback:', error);
        }
    });
    amplitudeReadyCallbacks = [];
};

export const isAmplitudeReady = () => {
    return isAmplitudeFullyInitialized;
};

export const logAmplitudeEvent = (eventName, eventProperties = {}) => {
    if (!isAmplitudeFullyInitialized) return;
    if (!APP_AMPLITUDE_API_KEY) return;

    const consentData = getConsentFromStorage();
    const hasConsent = consentData?.consent === true;

    try {
        const enrichedProperties = {
            ...eventProperties,
            consent_given: hasConsent,
            tracking_mode: hasConsent ? 'full' : 'anonymous',
        };

        amplitude.track(eventName, enrichedProperties);
    } catch (error) {
        console.error(`Error logging Amplitude event ${eventName}:`, error);
    }
};

const initializeFullAmplitude = () => {
    if (isAmplitudeFullyInitialized || !APP_AMPLITUDE_API_KEY) return;

    // Check if we have session continuity parameters from the widget
    const sessionParams = getSessionParamsFromUrl();

    try {
        const initOptions = {
            serverZone: 'EU',
            defaultTracking: false, // deprecated
            autocapture: {
                pageViews: false, // Disabled - not working with hash routing, using manual tracking
                sessions: true,
                formInteractions: false, // Disabled - not working properly with hash routing
            },
        };

        // If we have session continuity parameters, pass them to Amplitude
        if (sessionParams) {
            initOptions.deviceId = sessionParams.deviceId;
            initOptions.sessionId = sessionParams.sessionId;
        }

        amplitude.init(APP_AMPLITUDE_API_KEY, undefined, initOptions);
        isAmplitudeFullyInitialized = true;
    } catch (error) {
        return;
    }
};

/**
 * Initializes Amplitude in anonymous mode (GDPR compliant - no persistent identifiers)
 * Used when user has explicitly rejected cookie consent or dismissed banner without choosing
 */
export const initializeAnonymousAmplitude = () => {
    if (isAmplitudeFullyInitialized || !APP_AMPLITUDE_API_KEY) return;

    try {
        amplitude.init(APP_AMPLITUDE_API_KEY, undefined, {
            serverZone: 'EU',
            defaultTracking: false,
            autocapture: {
                pageViews: false, // We'll track manually
                sessions: false, // No session tracking without consent
            },
            identityStorage: 'none', // Don't store device ID in cookies/localStorage
            optOut: false, // Still track, but anonymously
        });
        isAmplitudeFullyInitialized = true;
    } catch (error) {
        return;
    }
};

const setConsentInStorage = (hasConsented) => {
    const consentData = {
        consent: hasConsented,
        timestamp: new Date().getTime(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
};

const getConsentFromStorage = () => {
    const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!storedConsent) {
        return null;
    }

    try {
        const consentData = JSON.parse(storedConsent);
        if (typeof consentData.consent !== 'boolean' || typeof consentData.timestamp !== 'number') {
            localStorage.removeItem(COOKIE_CONSENT_KEY);
            return null;
        }

        const expirationTime = consentData.timestamp + CONSENT_EXPIRATION_DAYS * 24 * 60 * 60 * 1000;
        if (new Date().getTime() > expirationTime) {
            localStorage.removeItem(COOKIE_CONSENT_KEY);
            return null; // Consent expired
        }

        return consentData;
    } catch (e) {
        localStorage.removeItem(COOKIE_CONSENT_KEY);
        return null;
    }
};
